/**
 * MSAL Adapter
 * Wraps MSAL instance to provide adapter interface for dependency injection
 * 
 * NOTE: This adapter must be created inside a React component/hook where useMsal can be called.
 * The adapter stores the MSAL instance and context at creation time.
 */

import type { MsalAdapter as IMsalAdapter } from '../types/adapters.types';
import type { IdentityProvider } from '../../../config/msal.config';
import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { createLoginRequest } from '../../../config/msal.config';
import { loginRequest } from '../../../config/msal.config';
import { clearMsalCacheSynchronously } from '../../../utils/msalCache.utils';

/**
 * Create MSAL adapter from MSAL instance and context
 *
 * @param instance - MSAL PublicClientApplication instance
 * @returns MsalAdapter implementation
 */
export function createMsalAdapter(
  instance: PublicClientApplication | null
): IMsalAdapter {
  return {
    loginRedirect: async (idp?: IdentityProvider, _returnTo?: string, prompt?: string) => {
      // Check if instance is ready
      if (!instance || typeof (instance as any).getConfiguration !== 'function') {
        throw new Error('MSAL instance is not ready yet. Please wait for initialization.');
      }

      // CRITICAL: For any explicit IdP (Google, Microsoft, etc.), clear full MSAL cache before redirect.
      // Otherwise cached account data can cause MSAL/Azure to show account picker with the wrong IdP
      // (e.g. Google account when user clicked "Sign in with Microsoft") and forward login_hint
      // to the wrong IdP, causing "parameter username is not supported" / Authorization Error.
      if (idp) {
        const currentAccount = instance.getActiveAccount();
        clearMsalCacheSynchronously(instance, currentAccount);
        // MSAL keeps an in-memory cache; clear it so loginRedirect() does not add account hint from cache.
        if (typeof (instance as any).clearCache === 'function') {
          await (instance as any).clearCache();
        }
      }

      // Create login request with optional IdP domain hint
      const request = idp ? createLoginRequest(idp) : loginRequest;

      // For explicit IdP selection, force fresh authentication (prompt=login) so Azure does not
      // show account picker with a previously used IdP or forward login_hint to the wrong IdP.
      const effectivePrompt = prompt ?? (idp ? 'login' : undefined);

      const signInRequest = {
        ...request,
        ...(effectivePrompt && { prompt: effectivePrompt }),
        extraQueryParameters: {
          ...request.extraQueryParameters,
          ...(prompt && { prompt }), // Caller-provided prompt only (effectivePrompt default is for MSAL top-level)
        },
      };
      // Note: domainHint is preserved from request - it's needed to route to Google/Apple IdP
      // Note: account and loginHint are already set to undefined in createLoginRequest for social IdP

      // Initiate redirect - navigation happens automatically
      await instance.loginRedirect(signInRequest);
    },
    logoutRedirect: async (postLogoutRedirectUri?: string, account?: AccountInfo | null) => {
      // Clear MSAL cookies (msal.cache.encryption etc.) - these are NOT cleared by storage.clear()
      try {
        document.cookie.split(';').forEach((c) => {
          const name = c.trim().split('=')[0];
          if (name && name.startsWith('msal.')) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        });
      } catch {
        // Ignore - cookies may be cross-origin or already cleared
      }

      // Check if instance is ready
      if (!instance || typeof (instance as any).getConfiguration !== 'function') {
        // Fallback: construct direct Azure logout URL
        const authority = import.meta.env.VITE_AZURE_AUTHORITY ?? 'https://login.microsoftonline.com/common';
        const logoutUrl = `${authority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
          postLogoutRedirectUri || `${window.location.origin}/login`
        )}`;
        window.location.href = logoutUrl;
        return;
      }
      
      // Use configured postLogoutRedirectUri or provided one
      const postLogoutUri =
        postLogoutRedirectUri ||
        instance.getConfiguration().auth.postLogoutRedirectUri ||
        `${window.location.origin}/login`;
      
      // CRITICAL: Pass the account to logoutRedirect so Azure clears the server-side session
      // for this specific user. Without this, Azure keeps the session and forwards login_hint
      // to Google on the next login attempt.
      await instance.logoutRedirect({
        account: account ?? undefined,
        postLogoutRedirectUri: postLogoutUri,
      });
    },
    getActiveAccount: () => {
      if (!instance || typeof (instance as any).getActiveAccount !== 'function') {
        return null;
      }
      return instance.getActiveAccount();
    },
    getAllAccounts: () => {
      if (!instance || typeof (instance as any).getAllAccounts !== 'function') {
        return [];
      }
      return instance.getAllAccounts();
    },
    isReady: () => {
      return !!(instance && typeof (instance as any).getConfiguration === 'function');
    },
    clearCache: async () => {
      if (!instance || typeof (instance as any).clearCache !== 'function') {
        return;
      }
      await (instance as any).clearCache();
    },
  };
}
